import React, { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const PerformanceComparisonChart = () => {
  const [selectedPeriod, setSelectedPeriod] = useState("3Y");

  const data = [
    { name: "Oct", year: "2022", growth: 100, equity: 100 },
    { name: "2023", year: "2023", growth: 108, equity: 106 },
    { name: "Apr", year: "2023", growth: 112, equity: 110 },
    { name: "Jul", year: "2023", growth: 120, equity: 118 },
    { name: "Oct", year: "2023", growth: 135, equity: 132 },
    { name: "2024", year: "2024", growth: 152, equity: 148 },
    { name: "Apr", year: "2024", growth: 172, equity: 168 },
    { name: "Jul", year: "2024", growth: 162, equity: 158 },
    { name: "Oct", year: "2024", growth: 195, equity: 188 },
    { name: "2025", year: "2025", growth: 220, equity: 208 },
    { name: "Apr", year: "2025", growth: 210, equity: 202 },
    { name: "Jul", year: "2025", growth: 208, equity: 189 },
  ];

  const periodButtons = ["1M", "1Y", "3Y", "5Y", "MAX", "SIP"];

  return (
    <div className="panel panel-default with-rounded-corner with-shadow" style={{ marginBottom: "20px" }}>
      {/* Header */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        padding: "15px 20px 10px",
        borderBottom: "1px solid #e5e5e5"
      }}>
        {/* Left side title */}
        <h3 style={{ 
          margin: 0,
          fontSize: "18px",
          fontWeight: "500",
          color: "#333"
        }}>
          Live Performance vs{" "}
          <span style={{ 
            color: "#007bff", 
            cursor: "pointer", 
            textDecoration: "none"
          }}>
            Equity Smallcap
          </span>
          <span style={{ marginLeft: "5px", color: "#007bff" }}>▼</span>
        </h3>

        {/* Period Filters */}
        <div style={{
          display: "flex",
          backgroundColor: "#f8f9fa",
          borderRadius: "6px",
          padding: "3px"
        }}>
          {periodButtons.map((period) => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              style={{
                padding: "6px 12px",
                fontSize: "12px",
                fontWeight: "500",
                borderRadius: "4px",
                border: "none",
                cursor: "pointer",
                transition: "all 0.2s",
                backgroundColor: selectedPeriod === period ? "#007bff" : "transparent",
                color: selectedPeriod === period ? "white" : "#666",
                minWidth: "35px"
              }}
              onMouseEnter={(e) => {
                if (selectedPeriod !== period) {
                  e.target.style.backgroundColor = "#e9ecef";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedPeriod !== period) {
                  e.target.style.backgroundColor = "transparent";
                }
              }}
            >
              {period}
            </button>
          ))}
        </div>
      </div>

      {/* Investment Summary */}
      <div style={{ padding: "15px 20px 10px" }}>
        <div style={{ 
          fontSize: "14px", 
          color: "#666", 
          marginBottom: "4px" 
        }}>
          Current value of ₹<span style={{ fontWeight: "600", color: "#333" }}>100</span> invested once
        </div>
        <div style={{ 
          fontSize: "14px", 
          color: "#666", 
          marginBottom: "12px" 
        }}>
          3 years ago would be
        </div>
        
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: "25px",
          flexWrap: "wrap"
        }}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "8px" 
          }}>
            <div style={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: "#007bff"
            }}></div>
            <span style={{ fontSize: "14px", color: "#666" }}>
              Growth & Income Model
            </span>
            <span style={{ fontWeight: "600", color: "#333" }}>₹ 207.77</span>
          </div>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "8px" 
          }}>
            <div style={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: "#fd7e14"
            }}></div>
            <span style={{ fontSize: "14px", color: "#666" }}>
              Equity Smallcap
            </span>
            <span style={{ fontWeight: "600", color: "#333" }}>₹ 188.91</span>
          </div>
        </div>
      </div>

      {/* Chart Container */}
      <div style={{ 
        width: "100%", 
        height: "280px", 
        padding: "0 15px" 
      }}>
        <ResponsiveContainer>
          <LineChart
            data={data}
            margin={{ top: 20, right: 20, bottom: 20, left: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={false}
              axisLine={false}
              domain={[100, 220]}
              ticks={[100, 120, 140, 160, 180, 200, 220]}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '1px solid #ddd', 
                borderRadius: '4px',
                fontSize: '12px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            />
            <Line
              type="monotone"
              dataKey="growth"
              stroke="#007bff"
              strokeWidth={2.5}
              dot={false}
              name="Growth & Income Model"
            />
            <Line
              type="monotone"
              dataKey="equity"
              stroke="#fd7e14"
              strokeWidth={2.5}
              dot={false}
              name="Equity Smallcap"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Note */}
      <div style={{ 
        padding: "10px 20px 15px",
        borderTop: "1px solid #f0f0f0"
      }}>
        <p style={{ 
          fontSize: "12px", 
          color: "#888", 
          margin: 0,
          lineHeight: "1.4"
        }}>
          <span style={{ fontWeight: "500" }}>Note:</span> Live performance includes rebalances. It is a tool to communicate factual & verifiable returns on behalf of the smallcase creator. It should not be considered as an advertisement, promotion or claim.
        </p>
      </div>
    </div>
  );
};

export default PerformanceComparisonChart;